import React, { useState, useEffect, startTransition, useCallback } from "react";
import {
  Button,
  Input,
  Form,
  DatePicker,
  Select,
  Row,
  Col,
  Space,
  Typography,
  Alert,
  Divider,
  InputNumber,
  Tag,
  Switch,
  Radio,
  Checkbox,
  Collapse
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import "./Invoices.css";
import DragUpload from "../../../commons/DragUpload/DragUpload";
import { Error, Success } from "../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../commons/Auth/auth";
import moment from "moment";
import MainButtonComp from "../../Buttons/MainButtonComp";
import { taxValue } from "../../../utils/taxValue";
import { convertStringToDate } from "../../../utils/convertStrToDate";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import GeneralModal from "../../../serviceProvider/components/General.modal";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  costTypeLabels,
  defaultCostType,
  invoiceTypeLabels,
  taxModeLabels
} from "../../../utils/invoiceStatusArray";
import {
  CURRENCIES,
  currencySymbol,
  formatCents
} from "../../../serviceProvider/components/accounting/shared/money";
import MoneyInput from "../../../serviceProvider/components/accounting/shared/MoneyInput";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { canAccounting, invoiceErrorText, preflightText } from "./invoiceErrors";
import * as recurringApi from "../../../serviceProvider/components/accounting/recurring/api";
import { INTERVALS } from "./RecurringInvoices";
import CustomerBranchPicker from "../CustomerBranchPicker";
import CustomerSelect from "../CustomerSelect";
import { findBranch, sameId } from "../customerBranches";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const TAX_MODE_HELP = {
  regular: "VAT is added per line (19 % or 7 %).",
  small_business: "No VAT is shown; the invoice carries the § 19 UStG note.",
  reverse_charge:
    "No VAT; the customer owes the tax (§ 13b UStG, construction services to a Bauleistender).",
  intra_eu: "Tax-free intra-community supply; the customer's VAT id is required.",
  eu_services: "Services to an EU business; the customer owes the VAT in their country.",
  exempt: "Tax-exempt service (§ 4 UStG)."
};

const emptyItem = () => ({
  description: "",
  price: "",
  duration: "",
  quantity: 1,
  unit: undefined,
  taxRate: undefined,
  costType: undefined
});

const SMALL_AMOUNT_LIMIT = 25000; // § 33 UStDV: 250 € gross

// "Repeat this invoice": saving creates a recurring invoice template (RI-1) instead of a single
// invoice; the daily job (or "run now" right after saving) writes one invoice per period.
const emptyRepeat = (enabled = false) => ({
  enabled,
  interval: "monthly",
  startDate: dayjs().startOf("month"),
  endDate: null,
  autoSend: false
});

const emptyExtras = () => ({
  discountMode: "percent",
  discountPercent: 0,
  discountAmount: null,
  retentionMode: "percent",
  retentionPercent: 0,
  retentionAmount: null,
  retentionDueDate: null,
  retentionGuarantee: false,
  directDebit: false,
  smallAmountInvoice: false,
  headerText: "",
  footerText: ""
});

/** Invoice-level discount spread over the rates in proportion to their net (like the server). */
const distributeDiscount = (groups, discountNet) => {
  const withNet = groups.filter(g => g.net > 0);
  const total = withNet.reduce((s, g) => s + g.net, 0);
  if (!discountNet || !total) return new Map();
  let rest = discountNet;
  const out = new Map();
  withNet.forEach((g, i) => {
    const part = i === withNet.length - 1 ? rest : Math.round((discountNet * g.net) / total);
    rest -= part;
    out.set(g.rate, part);
  });
  return out;
};

/** What a Schlussrechnung deducts for a related invoice: received (Abschlag) or invoiced amount. */
const deductionOf = c => {
  const byRate = c?.deduction?.byRate;
  if (Array.isArray(byRate) && byRate.length) {
    const net = byRate.reduce((s, g) => s + Number(g.net || 0), 0);
    const tax = byRate.reduce((s, g) => s + Number(g.tax || 0), 0);
    return { kind: c.deduction.kind, net, tax, gross: net + tax };
  }
  return {
    kind: c?.deduction?.kind || "invoiced",
    net: c?.amounts?.net || 0,
    tax: c?.amounts?.tax || 0,
    gross: c?.amounts?.gross || 0
  };
};

/** Cents-safe totals of the form: net per line = round(price × 100 × quantity), tax per rate group. */
const computeTotals = (items, { taxMode, defaultRate, advanceNet, type }, extras = {}) => {
  const lines =
    type === "advance" && !(items || []).some(i => parseFloat(i.price) > 0)
      ? [
          {
            net: Math.round(Number(advanceNet || 0)),
            rate: taxMode === "regular" ? defaultRate : 0
          }
        ]
      : (items || []).map(item => {
          const price = Math.max(0, parseFloat(item?.price) || 0);
          const quantity = Math.max(0, parseFloat(item?.quantity) || 0);
          // Same rounding as the server (utils/accounting/money#roundHalfUp): 15 significant
          // digits first, so 0.35 × 52.50 € is 18.38 € and not 18.37 €
          const net = Math.round(Number((price * 100 * quantity).toPrecision(15)));
          const rate =
            taxMode === "regular"
              ? item.taxRate === undefined || item.taxRate === null || item.taxRate === ""
                ? defaultRate
                : Number(item.taxRate)
              : 0;
          return { net, rate };
        });
  const byRate = new Map();
  for (const l of lines) byRate.set(l.rate, (byRate.get(l.rate) || 0) + l.net);
  const lineGroups = [...byRate.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([rate, net]) => ({ rate, net }));
  const linesNet = lineGroups.reduce((s, g) => s + g.net, 0);
  const discountNet = Math.min(
    Math.max(0, linesNet),
    extras.discountMode === "percent"
      ? Math.round((linesNet * Number(extras.discountPercent || 0)) / 100)
      : Math.max(0, Math.round(Number(extras.discountAmount || 0)))
  );
  const parts = distributeDiscount(lineGroups, discountNet);
  const groups = lineGroups.map(g => {
    const net = g.net - (parts.get(g.rate) || 0);
    return { rate: g.rate, net, tax: Math.round((net * g.rate) / 100) };
  });
  const net = groups.reduce((s, g) => s + g.net, 0);
  const tax = groups.reduce((s, g) => s + g.tax, 0);
  return { groups, linesNet, discountNet, net, tax, gross: net + tax };
};

const AddUpdateInvoices = ({
  isUpdate = false,
  wholeProject,
  updateParent,
  project,
  milestones,
  milestone,
  individualMilestone,
  openModal,
  closeModal,
  tasks,
  data,
  defaultRepeat = false,
  buttonText,
  onRecurringSaved
}) => {
  const { t } = useTranslation("dashboard");
  const { t: ta } = useTranslation("accounting");
  const { t: errorTranslation } = useTranslation("error");
  const isMobile = useIsMobile();

  const auth = isAuthenticated();
  const serviceProviderId = auth?.employer?._id ? auth.employer._id : auth?._id;

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([emptyItem()]);
  const [finalizing, setFinalizing] = useState(false);

  // Bookkeeping fields (module accounting)
  const [bk, setBk] = useState({
    type: "invoice",
    taxMode: "regular",
    defaultRate: taxValue || 19,
    dueDays: 14,
    skontoPercent: 0,
    skontoDays: 0,
    serviceDate: null,
    relatedInvoices: [],
    advanceNet: null
  });
  const [extras, setExtras] = useState(emptyExtras());
  const [repeat, setRepeat] = useState(emptyRepeat(defaultRepeat));
  const [customerInfo, setCustomerInfo] = useState(null);
  const [candidates, setCandidates] = useState([]);

  const [formData, setFormData] = useState({
    user: auth?._id,
    title: "",
    files: [],
    startDate: "",
    endDate: "",
    dueDate: "",
    tax: taxValue,
    currency: "EUR",
    description: "",
    project: "",
    createdBy: auth?._id,
    client: null
  });

  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientsArray, setClientsArray] = useState([]);
  // Niederlassung of the customer and the addressee ("branch" | "head_office")
  const [branchSel, setBranchSel] = useState({ clientBranch: null, billTo: "branch" });
  const selectedClient = clientsArray.find(c => sameId(c?._id, formData.client)) || null;
  // Document currency: changes the symbol on screen, PDF and e-invoice; amounts are booked as entered
  const currency = formData.currency || "EUR";
  const fmtMoney = cents => formatCents(cents, { currency });

  const setBkField = (key, value) => setBk(prev => ({ ...prev, [key]: value }));
  const setExtra = (key, value) => setExtras(prev => ({ ...prev, [key]: value }));
  const setRepeatField = (key, value) => setRepeat(prev => ({ ...prev, [key]: value }));
  // Only a new, ordinary invoice can repeat; milestone/task invoices bill one-off work
  const canRepeat =
    !isUpdate &&
    !data?._id &&
    !milestone &&
    !(Array.isArray(tasks) && tasks.length) &&
    !(Array.isArray(milestones) && milestones.length) &&
    canAccounting("Create");
  const repeating = canRepeat && repeat.enabled;

  useEffect(() => {
    if (!isModalOpen) return;
    if (data?._id) {
      form.setFieldsValue({
        title: data.title || "",
        client: data.client?._id || "",
        project: data.project?._id || "",
        startDate: parseDate(data.startDate),
        endDate: parseDate(data.endDate),
        dueDate: parseDate(data.dueDate),
        tax: data.tax != null ? Math.max(0, data.tax) : taxValue,
        currency: data.currency || "EUR",
        description: data.description || "",
        objectName: data.objectName || "",
        objectAddress: data.objectAddress || ""
      });
    } else {
      form.resetFields();
      // The required "Default VAT rate" field starts at the standard rate, like bk.defaultRate
      form.setFieldsValue({ tax: taxValue });
    }
  }, [isModalOpen]);

  const getAllClients = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users/clients/limited`,
        { pageSize: 10000000000, pageNo: 0, serviceProvider: serviceProviderId },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      if (res?.status === 200 || res?.status === 304)
        setClientsArray(Array.isArray(res.data?.data) ? res.data.data : []);
      else Error(res?.data?.errorMessage || "Failed to load clients");
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllClients();
  }, []);

  /** Customer defaults (payment terms, tax mode, e-invoice format) when a customer is picked. */
  const loadCustomerDefaults = useCallback(async (clientId, applyTerms) => {
    if (!clientId) return;
    try {
      const res = await accounting.customerDefaults(clientId);
      setCustomerInfo(res.data);
      if (applyTerms) {
        setBk(prev => ({
          ...prev,
          taxMode: res.data.taxMode || prev.taxMode,
          dueDays: res.data.paymentTerms?.dueDays ?? prev.dueDays,
          skontoPercent: res.data.paymentTerms?.skontoPercent ?? prev.skontoPercent,
          skontoDays: res.data.paymentTerms?.skontoDays ?? prev.skontoDays
        }));
      }
    } catch {
      // The accounting module may not be enabled for this user; the legacy form still works.
      setCustomerInfo(null);
    }
  }, []);

  /** Advance/partial invoices of this customer and project that a final invoice may deduct. */
  const loadCandidates = useCallback(async (clientId, projectId) => {
    if (!clientId) return setCandidates([]);
    try {
      const res = await accounting.relatedCandidates({
        client: clientId,
        project: projectId || undefined,
        exclude: data?._id
      });
      setCandidates(res.data.invoices || []);
    } catch {
      setCandidates([]);
    }
  }, []);

  useEffect(() => {
    if (bk.type === "final" && isModalOpen)
      loadCandidates(formData.client, project?._id || formData.project || data?.project?._id);
  }, [bk.type, formData.client, isModalOpen]);

  useEffect(() => {
    startTransition(() => {
      try {
        if (Array.isArray(tasks) && tasks.length > 0) {
          setItems(
            tasks.map(task => {
              const rawStart = Array.isArray(task?.startDate) ? task.startDate[0] : task?.startDate;
              const rawEnd = Array.isArray(task?.endDate)
                ? task.endDate[task.endDate.length - 1]
                : task?.endDate;
              const start = rawStart ? moment(rawStart, "DD-MM-YYYY") : null;
              const end = rawEnd ? moment(rawEnd, "DD-MM-YYYY") : null;
              const durationInHours =
                start && start.isValid() && end && end.isValid()
                  ? Math.max(0, end.diff(start, "hours"))
                  : 0;
              const price =
                data?.paymentType === "hourly" && data?.price
                  ? Math.max(0, durationInHours * parseFloat(data.price || 0))
                  : "";
              return {
                description: task?.title || "",
                price,
                duration: `${durationInHours} hours`,
                quantity: 1
              };
            })
          );
        } else if (Array.isArray(milestones) && milestones.length > 0) {
          setItems(
            milestones.map(ml => {
              const start = ml?.startDate ? moment(ml.startDate, "DD-MM-YYYY") : null;
              const end = ml?.endDate ? moment(ml.endDate, "DD-MM-YYYY") : null;
              const durationInDays =
                start && start.isValid() && end && end.isValid()
                  ? Math.max(0, end.diff(start, "days"))
                  : 0;
              return {
                description: ml?.name || "",
                price: ml?.price != null ? Math.max(0, ml.price) : "",
                duration: `${durationInDays} days`,
                quantity: 1,
                _id: ml?._id
              };
            })
          );
        }
        if (openModal) setIsModalOpen(true);
      } catch (err) {
        console.error("useEffect tasks/milestones error:", err);
      }
    });
  }, [openModal, tasks, milestones]);

  const totals = computeTotals(items, bk, extras);
  const selectedRelated = candidates.filter(c => bk.relatedInvoices.includes(String(c._id)));
  const deducted = selectedRelated.reduce(
    (s, c) => {
      const d = deductionOf(c);
      return { net: s.net + d.net, tax: s.tax + d.tax, gross: s.gross + d.gross };
    },
    { net: 0, tax: 0, gross: 0 }
  );

  const handleChange = (key, value) => {
    if (key === "files")
      return setFormData(prev => ({ ...prev, files: Array.isArray(value) ? value : [] }));
    if (key === "tax") {
      const num = parseFloat(value);
      if (!isNaN(num) && num < 0) return;
      setBkField("defaultRate", isNaN(num) ? 19 : num);
    }
    if (key === "client") {
      loadCustomerDefaults(value, !isUpdate);
      // The project's branch when the invoice is for the project's customer
      const fromProject = project && sameId(project.client, value) && project.clientBranch;
      setBranchSel(
        fromProject
          ? { clientBranch: project.clientBranch, billTo: project.billTo || "branch" }
          : { clientBranch: null, billTo: "branch" }
      );
    }
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleItemChange = (index, key, value) => {
    if (key === "price" || key === "quantity") {
      const num = parseFloat(value);
      if (!isNaN(num) && num < 0) return;
    }
    setItems(prev => {
      const newItems = Array.isArray(prev) ? [...prev] : [];
      if (!newItems[index]) newItems[index] = emptyItem();
      const next = { ...newItems[index], [key]: value };
      // § 35a EStG: the cost type follows the unit (hours → labour) until the user picks one
      if (key === "unit" && !next.costTypeManual) next.costType = defaultCostType(value);
      if (key === "costType") next.costTypeManual = true;
      newItems[index] = next;
      return newItems;
    });
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = index => setItems(prev => prev.filter((_, i) => i !== index));

  const parseDate = date => {
    if (!date) return null;
    const m = dayjs(date);
    return m.isValid() ? m : null;
  };

  const emptyFormData = {
    user: auth?._id,
    title: "",
    files: [],
    startDate: "",
    endDate: "",
    dueDate: "",
    tax: taxValue,
    currency: "EUR",
    description: "",
    project: "",
    objectName: "",
    objectAddress: "",
    createdBy: auth?._id,
    client: null
  };

  const showModal = () => {
    if (data?.lockedAt) {
      Error(
        ta("This invoice was sent and is locked. Cancel it with a cancellation invoice instead.")
      );
      return;
    }
    if (data?._id) {
      setFormData({
        ...emptyFormData,
        title: data.title || "",
        client: data.client?._id || null,
        project: data.project?._id || "",
        startDate: data.startDate || "",
        endDate: data.endDate || "",
        dueDate: data.dueDate || "",
        tax: data.tax != null ? Math.max(0, data.tax) : taxValue,
        currency: data.currency || "EUR",
        description: data.description || "",
        objectName: data.objectName || "",
        objectAddress: data.objectAddress || ""
      });
      setItems(
        Array.isArray(data.items)
          ? data.items.map(item => ({
              ...item,
              price:
                item?.unitPrice != null
                  ? item.unitPrice / 100
                  : item?.price != null
                    ? Math.max(0, parseFloat(item.price) || 0)
                    : "",
              quantity: item?.quantity != null ? Math.max(0, parseFloat(item.quantity) || 0) : 1,
              costType: item?.costType || defaultCostType(item?.unit),
              costTypeManual: Boolean(item?.costType)
            }))
          : []
      );
      setExtras({
        discountMode: data.discount?.amount && !data.discount?.percent ? "amount" : "percent",
        discountPercent: data.discount?.percent || 0,
        discountAmount: data.discount?.amount || null,
        retentionMode: data.retention?.percent
          ? "percent"
          : data.retention?.amount
            ? "amount"
            : "percent",
        retentionPercent: data.retention?.percent || 0,
        retentionAmount: data.retention?.percent ? null : data.retention?.amount || null,
        retentionDueDate: data.retention?.dueDate ? dayjs(data.retention.dueDate) : null,
        retentionGuarantee: Boolean(data.retention?.guarantee),
        directDebit: Boolean(data.directDebit?.enabled),
        smallAmountInvoice: Boolean(data.smallAmountInvoice),
        headerText: data.headerText || "",
        footerText: data.footerText || ""
      });
      setBk({
        type: data.type || "invoice",
        taxMode: data.taxMode || "regular",
        defaultRate: data.tax != null ? Number(data.tax) : 19,
        dueDays: data.paymentTerms?.dueDays ?? 14,
        skontoPercent: data.paymentTerms?.skontoPercent ?? 0,
        skontoDays: data.paymentTerms?.skontoDays ?? 0,
        serviceDate: data.serviceDate ? dayjs(data.serviceDate) : null,
        relatedInvoices: (data.relatedInvoices || []).map(r => String(r?._id || r)),
        advanceNet:
          data.type === "advance" && !(data.items || []).length ? (data.amounts?.net ?? null) : null
      });
      if (data.client?._id) loadCustomerDefaults(data.client._id, false);
      setBranchSel({ clientBranch: data.clientBranch || null, billTo: data.billTo || "branch" });
    } else {
      setFormData(emptyFormData);
      setItems([emptyItem()]);
      setBk({
        type: "invoice",
        taxMode: "regular",
        defaultRate: taxValue || 19,
        dueDays: 14,
        skontoPercent: 0,
        skontoDays: 0,
        serviceDate: null,
        relatedInvoices: [],
        advanceNet: null
      });
      setExtras(emptyExtras());
      setRepeat(emptyRepeat(defaultRepeat));
      setCustomerInfo(null);
      setBranchSel({ clientBranch: null, billTo: "branch" });
    }
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    closeModal && closeModal();
    setFormData(emptyFormData);
    setItems([emptyItem()]);
  };

  const buildPayload = values => {
    const safeItems = (Array.isArray(items) ? items : [])
      .filter(item => bk.type !== "advance" || parseFloat(item?.price) > 0 || item?.description)
      .map(raw => {
        const item = { ...raw };
        delete item.costTypeManual; // UI-only flag
        const price = Math.max(0, parseFloat(item?.price) || 0);
        return {
          ...item,
          costType: item.costType || defaultCostType(item.unit),
          price,
          unitPrice: Math.round(price * 100),
          quantity: Math.max(0, parseFloat(item?.quantity) || 0),
          taxRate:
            bk.taxMode === "regular"
              ? item.taxRate === undefined || item.taxRate === null || item.taxRate === ""
                ? bk.defaultRate
                : Number(item.taxRate)
              : 0
        };
      });
    const payload = {
      ...formData,
      ...values,
      tax: bk.taxMode === "regular" ? bk.defaultRate : 0,
      currency,
      files: Array.isArray(formData.files) ? formData.files.filter(f => f !== null) : [],
      items: safeItems,
      totalAmount: (totals.net / 100).toFixed(2),
      serviceProvider: serviceProviderId,
      project: project?._id || formData.project || data?.project?._id || undefined,
      milestone: milestone,
      clientBranch: branchSel.clientBranch || null,
      billTo: branchSel.billTo || "branch",
      type: bk.type,
      taxMode: bk.taxMode,
      paymentTerms: {
        dueDays: Number(bk.dueDays || 0),
        skontoPercent: Number(bk.skontoPercent || 0),
        skontoDays: Number(bk.skontoDays || 0)
      },
      serviceDate: bk.serviceDate ? dayjs(bk.serviceDate).format("YYYY-MM-DD") : "",
      relatedInvoices: bk.type === "final" ? bk.relatedInvoices : [],
      advanceNet:
        bk.type === "advance" && !safeItems.length
          ? Math.round(Number(bk.advanceNet || 0))
          : undefined,
      discount:
        extras.discountMode === "percent"
          ? { percent: Number(extras.discountPercent || 0), amount: 0 }
          : { percent: 0, amount: Math.round(Number(extras.discountAmount || 0)) },
      retention: {
        percent: extras.retentionMode === "percent" ? Number(extras.retentionPercent || 0) : 0,
        // A percentage is converted by the server (of the receivable after deductions)
        amount:
          extras.retentionMode === "amount" ? Math.round(Number(extras.retentionAmount || 0)) : 0,
        dueDate: extras.retentionDueDate ? dayjs(extras.retentionDueDate).format("YYYY-MM-DD") : "",
        guarantee: Boolean(extras.retentionGuarantee)
      },
      directDebit: { enabled: Boolean(extras.directDebit) },
      smallAmountInvoice: bk.type === "invoice" && Boolean(extras.smallAmountInvoice),
      headerText: extras.headerText || "",
      footerText: extras.footerText || ""
    };
    for (const key of ["startDate", "endDate", "dueDate"]) {
      if (values[key] && dayjs.isDayjs(values[key]))
        payload[key] = values[key].format("YYYY-MM-DD");
      else if (!payload[key]) delete payload[key];
    }
    payload.status = (
      individualMilestone ? data?.project?.financialController : data?.financialController
    )
      ? "In Control"
      : "Pending";
    return payload;
  };

  /** Saves the form as a recurring invoice template and creates the invoices already due. */
  const saveRecurring = async values => {
    const payload = buildPayload(values);
    const startDate = dayjs(repeat.startDate).format("YYYY-MM-DD");
    const body = {
      title: payload.title,
      client: payload.client,
      clientBranch: payload.clientBranch,
      billTo: payload.billTo,
      project: payload.project || null,
      items: payload.items.map(i => ({
        description: i.description,
        quantity: i.quantity,
        unit: i.unit || "",
        unitPrice: i.unitPrice,
        taxRate: i.taxRate,
        ...(i.costType ? { costType: i.costType } : {})
      })),
      taxMode: payload.taxMode,
      tax: String(payload.tax),
      currency: payload.currency,
      paymentTerms: payload.paymentTerms,
      description: payload.description || "",
      interval: repeat.interval,
      startDate,
      endDate: repeat.endDate ? dayjs(repeat.endDate).format("YYYY-MM-DD") : "",
      autoSend: Boolean(repeat.autoSend),
      directDebit: Boolean(extras.directDebit)
    };
    let template;
    try {
      template = (await recurringApi.createRecurring(body)).data?.template;
    } catch (err) {
      Error(recurringApi.errorText(err));
      return;
    }
    let created = 0;
    if (template?._id && startDate <= dayjs().format("YYYY-MM-DD")) {
      try {
        const runs = (await recurringApi.runRecurring(template._id)).data?.runs || [];
        created = runs.filter(r => r.invoice && !r.skipped).length;
        const errors = runs.filter(r => r.error).map(r => r.error);
        if (errors.length) Error(`${ta("Errors")}: ${errors.join("; ")}`);
      } catch (err) {
        Error(recurringApi.errorText(err));
      }
    }
    Success(
      created
        ? `${ta("Recurring invoice saved")} · ${ta("Invoices created")}: ${created}`
        : ta("Recurring invoice saved")
    );
    handleCancel();
    typeof updateParent === "function" && updateParent();
    typeof onRecurringSaved === "function" && onRecurringSaved(template);
  };

  const submitHandler = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    try {
      if (repeating) {
        await saveRecurring(values);
        return;
      }
      if (bk.type === "final" && !bk.relatedInvoices.length) {
        Error(ta("A final invoice needs at least one advance or partial invoice to deduct"));
        return;
      }
      const finalData = buildPayload(values);
      const headers = { authorization: "Bearer " + localStorage.getItem("token") };
      let savedId = data?._id;
      if (isUpdate) {
        const res = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/invoices/update/${data?._id}`,
          finalData,
          { headers }
        );
        if (res.status !== 200) return Error(res.data.errorMessage);
        Success(res.data.successMessage);
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/invoices/add`,
          finalData,
          { headers }
        );
        if (res?.status !== 200) return Error(res?.data?.errorMessage || "Failed to save invoice");
        savedId = res.data?.invoice?._id;
        const message =
          res.data?.code && errorTranslation(`invoice.${res.data?.code}`)
            ? errorTranslation("invoice.ci-100")
            : res.data?.successMessage || t("Saved successfully");
        Success(message);
      }
      if (finalizing && savedId) {
        const sent = await accounting.sendInvoice(savedId, { email: true });
        Success(`${ta("Invoice finalized")}: ${sent.data.invoice.invoiceNumber}`);
      }
      handleCancel();
      typeof updateParent === "function" && updateParent();
    } catch (err) {
      if (err?.response?.data?.code === "LOCKED")
        Error(
          ta("This invoice was sent and is locked. Cancel it with a cancellation invoice instead.")
        );
      else if (err?.response?.data?.code === "MISSING_MANDATORY") {
        // Saved as draft, but the Pflichtangaben check refused to send: list what is missing
        const missing = (Array.isArray(err.response.data.details) ? err.response.data.details : [])
          .filter(f => f.severity === "error")
          .map(f => preflightText(ta, f));
        Error([invoiceErrorText(ta, err), ...missing].join(" · "));
        handleCancel();
        typeof updateParent === "function" && updateParent();
      } else if (err?.response?.data?.code) Error(invoiceErrorText(ta, err));
      else if (err?.response?.data?.errorMessage) Error(err.response.data.errorMessage);
      else generalErrorAlert(err);
    } finally {
      setLoading(false);
      setFinalizing(false);
    }
  };

  const finalizeAndSend = () => {
    setFinalizing(true);
    form.submit();
  };

  const regular = bk.taxMode === "regular";
  const isAdvance = bk.type === "advance";
  const isFinal = bk.type === "final";
  const openAmount = totals.gross - (isFinal ? deducted.gross : 0);

  const retentionPreview =
    extras.retentionMode === "percent"
      ? Math.round((openAmount * Number(extras.retentionPercent || 0)) / 100)
      : Number(extras.retentionAmount || 0);
  const smallTooLarge = extras.smallAmountInvoice && totals.gross > SMALL_AMOUNT_LIMIT;

  const invoiceExtras = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={12}>
        <Form.Item
          label={ta("Invoice discount")}
          help={ta("Spread over the VAT rates in proportion to their net amounts")}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Radio.Group
              value={extras.discountMode}
              onChange={e => setExtra("discountMode", e.target.value)}
              optionType="button"
              options={[
                { value: "percent", label: "%" },
                { value: "amount", label: currencySymbol(currency) }
              ]}
            />
            {extras.discountMode === "percent" ? (
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                style={{ width: "100%" }}
                value={extras.discountPercent}
                onChange={v => setExtra("discountPercent", v ?? 0)}
              />
            ) : (
              <MoneyInput
                min={0}
                addonAfter={null}
                value={extras.discountAmount}
                onChange={v => setExtra("discountAmount", v)}
              />
            )}
          </Space.Compact>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          label={ta("Retention (Sicherheitseinbehalt)")}
          help={
            retentionPreview
              ? ta("{{amount}} is due only at the end of the warranty period", {
                  amount: fmtMoney(retentionPreview)
                })
              : ta("Part of the invoice the customer may keep until the warranty ends")
          }
        >
          <Space.Compact style={{ width: "100%" }}>
            <Radio.Group
              value={extras.retentionMode}
              onChange={e => setExtra("retentionMode", e.target.value)}
              optionType="button"
              options={[
                { value: "percent", label: "%" },
                { value: "amount", label: currencySymbol(currency) }
              ]}
            />
            {extras.retentionMode === "percent" ? (
              <InputNumber
                min={0}
                max={100}
                step={1}
                style={{ width: "100%" }}
                value={extras.retentionPercent}
                onChange={v => setExtra("retentionPercent", v ?? 0)}
              />
            ) : (
              <MoneyInput
                min={0}
                addonAfter={null}
                value={extras.retentionAmount}
                onChange={v => setExtra("retentionAmount", v)}
              />
            )}
          </Space.Compact>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label={ta("Retention due date")}>
          <DatePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            value={extras.retentionDueDate}
            onChange={d => setExtra("retentionDueDate", d)}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label=" " colon={false}>
          <Checkbox
            checked={extras.retentionGuarantee}
            onChange={e => setExtra("retentionGuarantee", e.target.checked)}
          >
            {ta("Replaced by a guarantee (Bürgschaft)")}
          </Checkbox>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          label={ta("Collect by SEPA direct debit")}
          help={ta("The customer needs a SEPA mandate; the PDF shows the mandate reference")}
        >
          <Switch
            checked={extras.directDebit}
            onChange={v => setExtra("directDebit", v)}
            aria-label={ta("Collect by SEPA direct debit")}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          label={ta("Small-amount invoice (§ 33 UStDV)")}
          help={
            bk.type !== "invoice"
              ? ta("Only for an ordinary invoice")
              : smallTooLarge
                ? ta("Only possible up to 250 € gross")
                : ta(
                    "Up to 250 € gross: the customer address and the separate tax amount may be omitted"
                  )
          }
          validateStatus={smallTooLarge ? "error" : undefined}
        >
          <Switch
            checked={extras.smallAmountInvoice}
            disabled={bk.type !== "invoice"}
            onChange={v => setExtra("smallAmountInvoice", v)}
            aria-label={ta("Small-amount invoice (§ 33 UStDV)")}
          />
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item label={ta("Header text")}>
          <TextArea
            rows={2}
            maxLength={2000}
            value={extras.headerText}
            onChange={e => setExtra("headerText", e.target.value)}
          />
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item label={ta("Footer text")}>
          <TextArea
            rows={2}
            maxLength={2000}
            value={extras.footerText}
            onChange={e => setExtra("footerText", e.target.value)}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const Invoicesitems = (
    <Form form={form} onFinish={submitHandler} layout="vertical" className="invoice-form">
      <Row gutter={[16, 0]}>
        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
          <Form.Item label={t("Title")} name="title" rules={[{ required: true }]}>
            <Input
              size="large"
              placeholder={t("Title")}
              onChange={e => handleChange("title", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
          <Form.Item label={t("Client")} name="client" rules={[{ required: true }]}>
            <CustomerSelect
              size="large"
              placeholder={t("Client")}
              customers={clientsArray}
              branch={branchSel.clientBranch}
              onChange={(id, branchId, customer) => {
                handleChange("client", id);
                if (branchId)
                  setBranchSel({
                    clientBranch: branchId,
                    billTo: findBranch(customer, branchId)?.defaultBillTo || "branch"
                  });
              }}
            />
          </Form.Item>
          {customerInfo?.customer ? (
            <Space wrap size={4} style={{ marginTop: -12, marginBottom: 12 }}>
              {customerInfo.customer.customerNumber ? (
                <Tag>
                  {ta("Customer number")}: {customerInfo.customer.customerNumber}
                </Tag>
              ) : null}
              {customerInfo.customer.isPublicClient ? (
                <Tag color="blue">XRechnung</Tag>
              ) : (
                <Tag>ZUGFeRD</Tag>
              )}
              {customerInfo.customer.is13bRecipient ? <Tag color="orange">§ 13b</Tag> : null}
              {customerInfo.customer.dunningBlocked ? (
                <Tag color="red">{ta("Dunning blocked")}</Tag>
              ) : null}
            </Space>
          ) : null}
        </Col>
        {selectedClient?.branches?.length ? (
          <Col xs={{ span: 24 }}>
            <CustomerBranchPicker
              customer={selectedClient}
              value={branchSel}
              onChange={setBranchSel}
              showBranchSelect={false}
            />
          </Col>
        ) : null}

        {/* Bookkeeping: type and tax mode */}
        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
          <Form.Item label={ta("Invoice type")}>
            <Select
              size="large"
              value={bk.type}
              onChange={v => setBkField("type", v)}
              disabled={
                repeating || (isUpdate && ["credit_note", "cancellation"].includes(bk.type))
              }
            >
              {["invoice", "partial", "advance", "final"].map(k => (
                <Option key={k} value={k}>
                  {ta(invoiceTypeLabels[k])}
                </Option>
              ))}
              {["credit_note", "cancellation"].includes(bk.type) ? (
                <Option value={bk.type}>{ta(invoiceTypeLabels[bk.type])}</Option>
              ) : null}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
          <Form.Item label={ta("Tax mode")} help={ta(TAX_MODE_HELP[bk.taxMode])}>
            <Select size="large" value={bk.taxMode} onChange={v => setBkField("taxMode", v)}>
              {Object.keys(taxModeLabels).map(k => (
                <Option key={k} value={k}>
                  {ta(taxModeLabels[k])}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
          <Form.Item label={t("Currency")}>
            <Select
              size="large"
              value={currency}
              onChange={v => setFormData(prev => ({ ...prev, currency: v }))}
            >
              {CURRENCIES.map(c => (
                <Option key={c} value={c}>
                  {t(c)} ({currencySymbol(c)})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {canRepeat ? (
          <Col xs={{ span: 24 }}>
            <div className="invoice-repeat">
              <Space align="center" wrap>
                <Switch
                  checked={repeat.enabled}
                  onChange={v => {
                    setRepeatField("enabled", v);
                    if (v) setBkField("type", "invoice");
                  }}
                  aria-label={ta("Repeat this invoice")}
                />
                <Typography.Text strong>{ta("Repeat this invoice")}</Typography.Text>
                <Typography.Text type="secondary">
                  {ta("For maintenance contracts, rent and other regular services")}
                </Typography.Text>
              </Space>
              {repeat.enabled ? (
                <Row gutter={[16, 0]} style={{ marginTop: 12 }}>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label={ta("Interval")}>
                      <Select
                        size="large"
                        value={repeat.interval}
                        onChange={v => setRepeatField("interval", v)}
                        options={INTERVALS.map(i => ({ value: i.value, label: ta(i.label) }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label={ta("First period starts")}
                      help={ta("Each invoice covers one period as its service period")}
                    >
                      <DatePicker
                        size="large"
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        allowClear={false}
                        value={repeat.startDate}
                        onChange={d => d && setRepeatField("startDate", d)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label={ta("End date")}>
                      <DatePicker
                        size="large"
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        placeholder={ta("Open-ended")}
                        value={repeat.endDate}
                        onChange={d => setRepeatField("endDate", d)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label={ta("Send automatically")}
                      help={ta("Off: a draft is created for review")}
                    >
                      <Switch
                        checked={repeat.autoSend}
                        onChange={v => setRepeatField("autoSend", v)}
                        aria-label={ta("Send automatically")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Alert
                      type="info"
                      showIcon
                      message={ta(
                        "Saving creates the invoices of every period that has already started. Discount, retention, header and footer texts, object and files are not repeated."
                      )}
                    />
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        ) : null}

        {/* Dates (a repeated invoice takes them from its period) */}
        {repeating ? null : (
          <>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <Form.Item label={t("Start Date")} name="startDate">
                <DatePicker
                  size="large"
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  onChange={(date, dateString) =>
                    handleChange(
                      "startDate",
                      date && dateString ? convertStringToDate(dateString) : ""
                    )
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <Form.Item label={t("End Date")} name="endDate">
                <DatePicker
                  size="large"
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  onChange={(date, dateString) =>
                    handleChange(
                      "endDate",
                      date && dateString ? convertStringToDate(dateString) : ""
                    )
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <Form.Item
                label={ta("Service date")}
                help={ta("Defaults to the end date or the invoice date")}
              >
                <DatePicker
                  size="large"
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  value={bk.serviceDate}
                  onChange={d => setBkField("serviceDate", d)}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {/* Payment terms */}
        <Col xs={{ span: 24 }} sm={{ span: 8 }}>
          <Form.Item label={ta("Payment term (days)")}>
            <InputNumber
              size="large"
              min={0}
              max={120}
              style={{ width: "100%" }}
              value={bk.dueDays}
              onChange={v => setBkField("dueDays", v ?? 0)}
            />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 8 }}>
          <Form.Item label={ta("Cash discount (%)")}>
            <InputNumber
              size="large"
              min={0}
              max={10}
              step={0.5}
              style={{ width: "100%" }}
              value={bk.skontoPercent}
              onChange={v => setBkField("skontoPercent", v ?? 0)}
            />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 8 }}>
          <Form.Item label={ta("Cash discount days")}>
            <InputNumber
              size="large"
              min={0}
              max={60}
              style={{ width: "100%" }}
              value={bk.skontoDays}
              onChange={v => setBkField("skontoDays", v ?? 0)}
            />
          </Form.Item>
        </Col>
        {repeating ? null : (
          <Col xs={{ span: 24 }} sm={{ span: 12 }}>
            <Form.Item
              label={t("Due Date")}
              name="dueDate"
              help={ta("Leave empty to compute it from the payment term")}
            >
              <DatePicker
                size="large"
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
                onChange={(date, dateString) =>
                  handleChange("dueDate", date && dateString ? convertStringToDate(dateString) : "")
                }
              />
            </Form.Item>
          </Col>
        )}
        {regular ? (
          <Col xs={{ span: 24 }} sm={{ span: 12 }}>
            <Form.Item label={ta("Default VAT rate")} name="tax" rules={[{ required: true }]}>
              <Select size="large" onChange={v => handleChange("tax", v)}>
                <Option value={19}>19 %</Option>
                <Option value={7}>7 %</Option>
                <Option value={0}>0 %</Option>
              </Select>
            </Form.Item>
          </Col>
        ) : null}

        <Col xs={{ span: 24 }}>
          <Row gutter={[16, 0]}>
            <Col xs={{ span: 24 }} sm={{ span: 12 }}>
              <Form.Item label={t("Object Name")} name="objectName">
                <Input
                  size="large"
                  placeholder={t("Object Name")}
                  onChange={e => handleChange("objectName", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} sm={{ span: 12 }}>
              <Form.Item label={t("Object Address")} name="objectAddress">
                <Input
                  size="large"
                  placeholder={t("Object Address")}
                  onChange={e => handleChange("objectAddress", e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* Schlussrechnung: deducted advance invoices */}
        {isFinal ? (
          <Col xs={{ span: 24 }}>
            <Form.Item
              label={ta("Advance / partial invoices to deduct")}
              help={ta(
                "All sent advance and partial invoices of this customer and project must be listed on the final invoice (§ 14 Abs. 5 UStG)"
              )}
            >
              <Select
                mode="multiple"
                size="large"
                value={bk.relatedInvoices}
                onChange={v => setBkField("relatedInvoices", v)}
                placeholder={
                  candidates.length
                    ? ta("Select invoices")
                    : ta("No sent advance invoices found for this customer")
                }
                optionFilterProp="children"
              >
                {candidates.map(c => (
                  <Option key={String(c._id)} value={String(c._id)}>
                    {c.invoiceNumber} – {c.title} ({fmtMoney(c.amounts?.gross || 0)}
                    {c.deduction?.kind === "received"
                      ? `, ${ta("received")} ${fmtMoney(deductionOf(c).gross)}`
                      : ""}
                    )
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {selectedRelated.length ? (
              <Alert
                type="info"
                showIcon
                message={ta("Deduction preview")}
                description={
                  <div>
                    {selectedRelated.map(c => {
                      const d = deductionOf(c);
                      return (
                        <div key={String(c._id)} className="deduction-line">
                          <div>
                            {c.invoiceNumber}: {fmtMoney(d.net)} {ta("net")} + {fmtMoney(d.tax)}{" "}
                            {ta("VAT")} = - {fmtMoney(d.gross)}
                          </div>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {d.kind === "received"
                              ? ta(
                                  "Received amount deducted ({{received}} of {{invoiced}} invoiced)",
                                  {
                                    received: fmtMoney(d.gross),
                                    invoiced: fmtMoney(c.amounts?.gross || 0)
                                  }
                                )
                              : ta("Invoiced amount deducted")}
                          </Typography.Text>
                        </div>
                      );
                    })}
                    <strong>
                      {ta("Total deducted")}: - {fmtMoney(deducted.gross)}
                    </strong>
                  </div>
                }
              />
            ) : null}
          </Col>
        ) : null}

        <Col xs={{ span: 24 }}>
          <Form.Item label={t("Files")}>
            <DragUpload
              accept="image/*"
              value={formData?.files}
              updateFiles={val => handleChange("files", Array.isArray(val) ? val : [])}
            />
          </Form.Item>
        </Col>

        {/* Items */}
        <Col xs={{ span: 24 }}>
          <div className="ManageInvoices">
            <div className="items-section">
              <h3 className="text-start">{t("Items")}</h3>
              {isAdvance ? (
                <Form.Item
                  label={ta("Advance amount (net)")}
                  help={ta(
                    "An advance invoice may carry a net amount without line items; VAT is added according to the tax mode"
                  )}
                >
                  <MoneyInput
                    size="large"
                    currency={currency}
                    value={bk.advanceNet}
                    onChange={v => setBkField("advanceNet", v)}
                  />
                </Form.Item>
              ) : null}
              {Array.isArray(items) &&
                items.map((item, index) => {
                  const lineNet = fmtMoney(
                    Math.round(
                      (parseFloat(item.price) || 0) * 100 * (parseFloat(item.quantity) || 0)
                    )
                  );
                  const descriptionField = (
                    <Input
                      size="large"
                      placeholder={t("Name")}
                      aria-label={t("Name")}
                      value={item.description}
                      disabled={wholeProject && item?._id}
                      onChange={e => handleItemChange(index, "description", e.target.value)}
                    />
                  );
                  const quantityField = (
                    <Input
                      size="large"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      placeholder={ta("Quantity")}
                      aria-label={ta("Quantity")}
                      value={item.quantity}
                      onChange={e => handleItemChange(index, "quantity", e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "-") e.preventDefault();
                      }}
                    />
                  );
                  const unitField = (
                    <Select
                      style={{ width: "100%" }}
                      size="large"
                      placeholder={t("Unit")}
                      aria-label={t("Unit")}
                      value={item.unit || undefined}
                      disabled={wholeProject && item?._id}
                      onChange={e => handleItemChange(index, "unit", e)}
                    >
                      <Select.Option value="Stück">Stück</Select.Option>
                      <Select.Option value="Cm">{t("Cm")}</Select.Option>
                      <Select.Option value="M">M</Select.Option>
                      <Select.Option value="m²">m²</Select.Option>
                      <Select.Option value="KM">{t("KM")}</Select.Option>
                      <Select.Option value="std.">std.</Select.Option>
                      <Select.Option value="Minute">{t("Minute")}</Select.Option>
                      <Select.Option value="Hour">{t("Hour")}</Select.Option>
                      <Select.Option value="Tag">Tag</Select.Option>
                      <Select.Option value="kg">kg</Select.Option>
                      <Select.Option value="Pauschale">{t("Pauschale")}</Select.Option>
                    </Select>
                  );
                  const priceField = (
                    <Input
                      size="large"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      placeholder={`${ta("Unit price (net)")} ${currencySymbol(currency)}`}
                      aria-label={ta("Unit price (net)")}
                      value={item.price}
                      onChange={e => handleItemChange(index, "price", e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "-") e.preventDefault();
                      }}
                    />
                  );
                  const taxField = regular ? (
                    <Select
                      style={{ width: "100%" }}
                      size="large"
                      aria-label={ta("VAT")}
                      value={
                        item.taxRate === undefined || item.taxRate === null || item.taxRate === ""
                          ? bk.defaultRate
                          : Number(item.taxRate)
                      }
                      onChange={v => handleItemChange(index, "taxRate", v)}
                    >
                      {/* The phone card labels the field "VAT", so the options drop the suffix. */}
                      <Select.Option value={19}>{isMobile ? "19 %" : "19 % USt"}</Select.Option>
                      <Select.Option value={7}>{isMobile ? "7 %" : "7 % USt"}</Select.Option>
                      <Select.Option value={0}>{isMobile ? "0 %" : "0 % USt"}</Select.Option>
                    </Select>
                  ) : (
                    <Input
                      size="large"
                      placeholder={t("Duration")}
                      aria-label={t("Duration")}
                      value={item.duration}
                      onChange={e => handleItemChange(index, "duration", e.target.value)}
                    />
                  );

                  const costTypeField = (
                    <Select
                      style={{ width: "100%" }}
                      size="large"
                      aria-label={ta("Cost type")}
                      value={item.costType || defaultCostType(item.unit)}
                      onChange={v => handleItemChange(index, "costType", v)}
                      options={Object.entries(costTypeLabels).map(([value, label]) => ({
                        value,
                        label: ta(label)
                      }))}
                    />
                  );

                  if (isMobile) {
                    const field = (label, control) => (
                      <label className="item-card__field">
                        <span className="item-card__label">{label}</span>
                        {control}
                      </label>
                    );
                    return (
                      <div key={index} className="item-card">
                        <div className="item-card__head">
                          <span className="item-card__index">
                            {ta("Position")} {index + 1}
                          </span>
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            aria-label={t("Remove")}
                            className="item-card__remove"
                            onClick={() => removeItem(index)}
                          />
                        </div>
                        {field(t("Name"), descriptionField)}
                        <div className="item-card__grid">
                          {field(ta("Quantity"), quantityField)}
                          {field(ta("Unit price (net)"), priceField)}
                          {field(regular ? ta("VAT") : t("Duration"), taxField)}
                        </div>
                        {field(ta("Cost type"), costTypeField)}
                        <div className="item-card__foot">
                          <div className="item-card__unit">{field(t("Unit"), unitField)}</div>
                          <div className="item-card__total">
                            <span className="item-card__label">{ta("Net")}</span>
                            <strong>{lineNet}</strong>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Row key={index} gutter={[16, 16]} className="item-row">
                      <Col xs={24} sm={8}>
                        {descriptionField}
                      </Col>
                      <Col xs={24} sm={8}>
                        {quantityField}
                      </Col>
                      <Col xs={24} sm={8}>
                        {unitField}
                      </Col>
                      <Col xs={24} sm={6}>
                        {priceField}
                      </Col>
                      <Col xs={24} sm={6}>
                        {taxField}
                      </Col>
                      <Col xs={24} sm={6}>
                        {costTypeField}
                      </Col>
                      <Col xs={24} sm={6}>
                        <Space wrap>
                          <Typography.Text type="secondary">{lineNet}</Typography.Text>
                          <Button danger onClick={() => removeItem(index)}>
                            {t("Remove")}
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  );
                })}
              <Space direction="vertical">
                <Button type="dashed" onClick={addItem}>
                  {t("Add Item")}
                </Button>
              </Space>
              <Divider />
              <div className="items-totals">
                {totals.discountNet ? (
                  <>
                    <Row justify="space-between">
                      <Col>{ta("Lines net")}</Col>
                      <Col>{fmtMoney(totals.linesNet)}</Col>
                    </Row>
                    <Row justify="space-between">
                      <Col>{ta("Invoice discount")}</Col>
                      <Col>- {fmtMoney(totals.discountNet)}</Col>
                    </Row>
                  </>
                ) : null}
                <Row justify="space-between">
                  <Col>{ta("Net")}</Col>
                  <Col>{fmtMoney(totals.net)}</Col>
                </Row>
                {totals.groups
                  .filter(g => g.rate > 0)
                  .map(g => (
                    <Row justify="space-between" key={g.rate}>
                      <Col>
                        {ta("VAT")} {g.rate} %
                      </Col>
                      <Col>{fmtMoney(g.tax)}</Col>
                    </Row>
                  ))}
                {!regular ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {ta(TAX_MODE_HELP[bk.taxMode])}
                  </Typography.Text>
                ) : null}
                <Row justify="space-between">
                  <Col>
                    <strong>{ta("Gross")}</strong>
                  </Col>
                  <Col>
                    <strong>{fmtMoney(totals.gross)}</strong>
                  </Col>
                </Row>
                {isFinal ? (
                  <>
                    <Row justify="space-between">
                      <Col>{ta("Less advance invoices")}</Col>
                      <Col>- {fmtMoney(deducted.gross)}</Col>
                    </Row>
                    <Row justify="space-between">
                      <Col>
                        <Title level={5}>{ta("Remaining amount due")}</Title>
                      </Col>
                      <Col>
                        <Title level={5}>{fmtMoney(openAmount)}</Title>
                      </Col>
                    </Row>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={{ span: 24 }}>
          <Collapse
            className="invoice-extras"
            defaultActiveKey={
              extras.discountPercent ||
              extras.discountAmount ||
              extras.retentionPercent ||
              extras.retentionAmount ||
              extras.directDebit ||
              extras.smallAmountInvoice ||
              extras.headerText ||
              extras.footerText
                ? ["extras"]
                : []
            }
            items={[
              {
                key: "extras",
                label: ta("Discount, retention, payment and texts"),
                children: invoiceExtras
              }
            ]}
          />
        </Col>

        <Col xs={{ span: 24 }}>
          <Form.Item label={t("Description")} name="description">
            <TextArea
              placeholder={t("Description")}
              onChange={e => handleChange("description", e.target.value)}
              rows={4}
            />
          </Form.Item>
        </Col>
        {isUpdate || data?._id ? (
          <Col xs={{ span: 24 }}>
            <Alert
              type="warning"
              showIcon
              className="finalize-alert"
              message={ta(
                "Finalizing allocates the invoice number, posts the invoice to the ledger, creates the PDF with embedded e-invoice and locks it"
              )}
              action={
                <Button type="primary" onClick={finalizeAndSend} loading={loading && finalizing}>
                  {ta("Finalize & send")}
                </Button>
              }
            />
          </Col>
        ) : null}
      </Row>
    </Form>
  );

  return (
    <>
      {(openModal || !wholeProject) && (
        <>
          {isUpdate ? (
            <MainButtonComp text="Edit" editBtn onClick={showModal} />
          ) : (
            <MainButtonComp text={buttonText || "Add Invoice"} addBtn onClick={showModal} />
          )}
        </>
      )}
      <GeneralModal
        title={
          isUpdate ? "Update Invoice" : repeating ? ta("New recurring invoice") : "Add Invoice"
        }
        onOk={() => form.submit()}
        open={isModalOpen}
        okText={t("Save")}
        onCancel={handleCancel}
        items={Invoicesitems}
      />
    </>
  );
};

export default AddUpdateInvoices;
